import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PlusIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { quotesApi, type QuoteWithRelations } from '../../lib/api'
import { Pagination } from '../../components/ui/Pagination'

type SortField = 'quoteNumber' | 'account' | 'docTotal' | 'status' | 'validUntil'
type SortDirection = 'asc' | 'desc'

const QUOTE_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
] as const

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  approved: { bg: 'bg-blue-100', text: 'text-blue-800' },
  sent: { bg: 'bg-purple-100', text: 'text-purple-800' },
  accepted: { bg: 'bg-green-100', text: 'text-green-800' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800' },
  expired: { bg: 'bg-orange-100', text: 'text-orange-800' },
}

export function QuotesListPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('quoteNumber')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Build filters object for API
  const filters: Record<string, string> = {}
  if (statusFilter !== 'all') filters.status = statusFilter
  if (searchQuery) filters.search = searchQuery

  const { data: response, isLoading } = useQuery({
    queryKey: ['quotes', { page, limit, filters }],
    queryFn: () => quotesApi.list({ page, limit, filters: Object.keys(filters).length > 0 ? filters : undefined }),
  })

  const { data: stats } = useQuery({
    queryKey: ['quotes', 'stats'],
    queryFn: () => quotesApi.getStats(),
  })

  const quotes = response?.data

  // Handle filter changes - reset to page 1
  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    setPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection(field === 'quoteNumber' || field === 'validUntil' ? 'desc' : 'asc')
    }
  }

  // Client-side sorting
  const sortedQuotes = useMemo(() => {
    if (!quotes) return []

    const result = [...quotes]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'quoteNumber':
          comparison = a.quoteNumber.localeCompare(b.quoteNumber)
          break
        case 'account':
          const nameA = a.account?.name || ''
          const nameB = b.account?.name || ''
          comparison = nameA.localeCompare(nameB)
          break
        case 'docTotal':
          comparison = parseFloat(a.docTotal || '0') - parseFloat(b.docTotal || '0')
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        case 'validUntil':
          comparison = new Date(a.validUntil).getTime() - new Date(b.validUntil).getTime()
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [quotes, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  const formatPrice = (price: string | null, currency: string = 'EUR') => {
    if (!price) return '-'
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(parseFloat(price))
  }

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const isExpired = (validUntil: Date | string) => {
    return new Date(validUntil) < new Date()
  }

  if (isLoading) return <div className="p-8">Loading...</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Quotes</h2>
            <p className="mt-1 text-sm text-gray-500">
              Create and manage customer quotes
            </p>
          </div>

          <Link
            to="/quotes/new"
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            New Quote
          </Link>

          {/* Search */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Search</h3>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by quote number..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Status</h3>
            <div className="flex flex-wrap gap-2">
              {QUOTE_STATUSES.slice(0, 5).map(status => (
                <button
                  key={status.value}
                  onClick={() => handleStatusFilter(status.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {QUOTE_STATUSES.slice(5).map(status => (
                <button
                  key={status.value}
                  onClick={() => handleStatusFilter(status.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    statusFilter === status.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Stats</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Draft</dt>
                <dd className="font-medium text-gray-900">{stats?.draft || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Pending Approval</dt>
                <dd className="font-medium text-gray-900">{stats?.pending_approval || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Sent</dt>
                <dd className="font-medium text-gray-900">{stats?.sent || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Accepted</dt>
                <dd className="font-medium text-green-600">{stats?.accepted || 0}</dd>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">{stats?.total || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Quotes</h1>
            <Link
              to="/quotes/new"
              className="mt-4 w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
            >
              <PlusIcon className="h-5 w-5" />
              New Quote
            </Link>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <SortableHeader field="quoteNumber">Quote #</SortableHeader>
                  <SortableHeader field="account">Account</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Items</th>
                  <SortableHeader field="docTotal">Total</SortableHeader>
                  <SortableHeader field="status">Status</SortableHeader>
                  <SortableHeader field="validUntil">Valid Until</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedQuotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/quotes/${quote.id}`} className="hover:text-brand-600 flex items-center gap-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        {quote.quoteNumber}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {quote.account ? (
                        <Link to={`/accounts/${quote.accountId}`} className="hover:text-brand-600">
                          {quote.account.name}
                        </Link>
                      ) : (
                        quote.cardName || '-'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {quote._count?.lineItems || 0}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      {formatPrice(quote.docTotal, quote.docCurrency)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[quote.status]?.bg || 'bg-gray-100'} ${STATUS_COLORS[quote.status]?.text || 'text-gray-700'}`}>
                        {quote.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={isExpired(quote.validUntil) && quote.status !== 'accepted' ? 'text-red-600' : ''}>
                        {formatDate(quote.validUntil)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link
                        to={`/quotes/${quote.id}`}
                        className="text-brand-600 hover:text-brand-900"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {sortedQuotes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                      No quotes found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
